import { Component, OnInit } from '@angular/core';
import { ChartDataSets, ChartOptions } from 'chart.js';
import { Color, Label } from 'ng2-charts';
import { CompanyType } from 'situview-webapi-client';
import { AuthService, StateService } from 'src/app/services';
import { SituBaseComponent } from 'src/app/shared/situ-base-component';

interface ProductRow {
  name: string;
  builders: number;
  impressions: number;
  opens: number;
  change: number; // % vs the previous month
}

interface ShareRow {
  name: string;
  impressions: number;
}

interface CategoryRow {
  name: string;
  suppliers: number;
  categoryImpressions: number;
  categoryChange: number; // % vs the previous month, platform-wide
  yourImpressions: number;
}

/**
 * MOCKUP: the v1 supplier dashboard (Letitia's supplier reporting + situ PULSE ask).
 * Everything here is hard-coded sample data; nothing is fetched. The real version reads a
 * per-supplier monthly summary saved by the SituAnalytics monthly run (the same numbers as
 * the supplier PDF), with the company taken from the login on the server.
 */
@Component({
  selector: 'supplier-dashboard',
  templateUrl: './supplier-dashboard.component.html',
  styleUrls: ['./supplier-dashboard.component.scss'],
})
export class SupplierDashboardComponent extends SituBaseComponent implements OnInit {
  readonly months = ['September 2026', 'August 2026', 'July 2026', 'June 2026'];
  selectedMonth = this.months[0];

  readonly kpis = {
    impressions: 48210,
    impressionsChange: 7.1,
    opens: 312,
    opensChange: 12.4,
    productsSeen: 37,
    productsLive: 52,
    builders: 4,
    buildersNew: 1,
  };

  // 12-month trend, Oct 2025 to Sep 2026.
  readonly trendLabels: Label[] = ['Oct', 'Nov', 'Dec', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep'];
  private readonly trendImpressions = [31240, 29410, 22105, 27380, 33020, 36110, 38450, 41020, 43870, 41960, 45010, 48210];
  private readonly trendOpens = [188, 171, 120, 166, 204, 231, 240, 259, 281, 262, 278, 312];
  trendMeasure: 'impressions' | 'opens' = 'impressions';
  trendData: ChartDataSets[] = [];
  readonly trendColors: Color[] = [{ backgroundColor: '#e6195a', hoverBackgroundColor: '#b8104a', borderWidth: 0 }];
  readonly trendOptions: ChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    legend: { display: false },
    scales: {
      // Mid-grey ticks and grid read on both the light and dark portal themes.
      xAxes: [{ ticks: { fontColor: '#8a8f98' }, gridLines: { display: false } }],
      yAxes: [{ ticks: { fontColor: '#8a8f98', beginAtZero: true, maxTicksLimit: 5, callback: (v: number) => v.toLocaleString() }, gridLines: { color: 'rgba(128,128,128,.18)', zeroLineColor: 'rgba(128,128,128,.4)' } }],
    },
    tooltips: { callbacks: { label: (item) => `${Number(item.yLabel).toLocaleString()} ${this.trendMeasure === 'impressions' ? 'impressions' : 'showcase opens'}` } },
  };

  readonly productColumns = ['product', 'builders', 'impressions', 'opens', 'openRate', 'change'];
  readonly products: ProductRow[] = [
    { name: 'Basin mixer – matte black', builders: 4, impressions: 11820, opens: 74, change: 9.2 },
    { name: 'Wall-mounted bath spout – chrome', builders: 3, impressions: 8140, opens: 41, change: 3.5 },
    { name: 'Rail shower set – brushed nickel', builders: 4, impressions: 7615, opens: 66, change: 18.0 },
    { name: 'Kitchen sink mixer – pull-out', builders: 2, impressions: 6230, opens: 52, change: -4.1 },
    { name: 'Towel rail – heated, 600 mm', builders: 3, impressions: 4980, opens: 29, change: 1.7 },
    { name: 'Laundry trough tap – chrome', builders: 2, impressions: 3105, opens: 11, change: -8.6 },
  ];
  readonly otherProducts = { count: 31, impressions: 6320, opens: 39 };

  // Where the supplier's products were seen. Builder names are fine to show (Letitia, Oct 2026).
  readonly builders: ShareRow[] = [
    { name: '[Builder A]', impressions: 21480 },
    { name: '[Builder B]', impressions: 14210 },
    { name: '[Builder C]', impressions: 8930 },
    { name: '[Builder D]', impressions: 3590 },
  ];
  readonly homeDesigns: ShareRow[] = [
    { name: '[Home design 1]', impressions: 6120 },
    { name: '[Home design 2]', impressions: 5480 },
    { name: '[Home design 3]', impressions: 4390 },
    { name: '[Home design 4]', impressions: 3870 },
    { name: '[Home design 5]', impressions: 3010 },
  ];
  readonly themes: ShareRow[] = [
    { name: '[Theme 1]', impressions: 13240 },
    { name: '[Theme 2]', impressions: 11080 },
    { name: '[Theme 3]', impressions: 9650 },
    { name: '[Theme 4]', impressions: 7720 },
    { name: '[Theme 5]', impressions: 6520 },
  ];
  readonly rooms: ShareRow[] = [
    { name: 'Ensuite', impressions: 16330 },
    { name: 'Main bathroom', impressions: 13950 },
    { name: 'Kitchen', impressions: 8740 },
    { name: 'Laundry', impressions: 5120 },
    { name: 'Powder room', impressions: 4070 },
  ];

  // Category context from situ PULSE. Only categories with at least 5 suppliers are shown.
  readonly minSuppliers = 5;
  readonly categories: CategoryRow[] = [
    { name: 'Tapware & bathroom', suppliers: 9, categoryImpressions: 126400, categoryChange: 5.8, yourImpressions: 41890 },
    { name: 'Benchtops & cabinetry', suppliers: 7, categoryImpressions: 88300, categoryChange: -2.3, yourImpressions: 6320 },
  ];

  // The supplier's own impressions by state (from the stored town/state, never IP addresses).
  readonly states: ShareRow[] = [
    { name: 'Victoria', impressions: 19760 },
    { name: 'Queensland', impressions: 13020 },
    { name: 'New South Wales', impressions: 7710 },
    { name: 'South Australia', impressions: 5300 },
    { name: 'Other', impressions: 2420 },
  ];

  constructor(authService: AuthService, stateService: StateService) {
    super(authService, stateService);
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.setTrendMeasure('impressions');
  }

  // A builder (e.g. Burbank) picked in the company selector would read as the supplier, so only
  // manufacturers lend their name to the mockup.
  get supplierName(): string {
    const sample = 'Sample Tapware Co.';
    return this.selectedCompany?.type === CompanyType.Manufacturer ? this.selectedCompany.name ?? sample : sample;
  }

  get oneInOpens(): number {
    return Math.round(this.kpis.impressions / this.kpis.opens);
  }

  setTrendMeasure(measure: 'impressions' | 'opens'): void {
    this.trendMeasure = measure;
    this.trendData = [{
      data: measure === 'impressions' ? this.trendImpressions : this.trendOpens,
      label: measure === 'impressions' ? 'Impressions' : 'Showcase opens',
      barPercentage: 0.6,
    }];
  }

  openRate(row: { impressions: number; opens: number }): string {
    return row.impressions ? `1 in ${Math.round(row.impressions / Math.max(1, row.opens))}` : '—';
  }

  share(row: ShareRow, rows: ShareRow[]): number {
    const total = rows.reduce((a, r) => a + r.impressions, 0) || 1;
    return (row.impressions / total) * 100;
  }

  barWidth(row: ShareRow, rows: ShareRow[]): string {
    const max = Math.max(...rows.map((r) => r.impressions), 1);
    return `${((row.impressions / max) * 100).toFixed(1)}%`;
  }

  categoryShare(c: CategoryRow): number {
    return (c.yourImpressions / c.categoryImpressions) * 100;
  }
}
